import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Eye, MapPin } from "@phosphor-icons/react";

export default function LocationPrivacyDialog({ open, onClose, meId }) {
  const [members, setMembers] = useState([]);
  const [seesMe, setSeesMe] = useState({});
  const [iSee, setISee] = useState({});

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/location/privacy"), api.get("/family")]).then(([p, f]) => {
      const others = (f.data.family?.members || []).filter((m) => m.id !== meId);
      setMembers(others);
      const { mode, allowed, hidden } = p.data;
      const sm = {}, is = {};
      others.forEach((m) => {
        sm[m.id] = mode === "all" ? true : mode === "none" ? false : allowed.includes(m.id);
        is[m.id] = !hidden.includes(m.id);
      });
      setSeesMe(sm);
      setISee(is);
    }).catch(() => {});
  }, [open, meId]);

  const save = async () => {
    const allowedIds = members.filter((m) => seesMe[m.id]).map((m) => m.id);
    const mode = allowedIds.length === members.length ? "all" : allowedIds.length === 0 ? "none" : "custom";
    const hidden = members.filter((m) => !iSee[m.id]).map((m) => m.id);
    try {
      await api.put("/location/privacy", { mode, allowed: allowedIds, hidden });
      toast.success("Настройки приватности сохранены");
      onClose(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="glass-modal">
        <DialogHeader><DialogTitle>Приватность передвижений</DialogTitle></DialogHeader>
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground">В семье пока нет других участников — пригласите их по коду в разделе «Семья».</p>
        ) : (
          <div className="space-y-1" data-testid="privacy-matrix">
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-5 items-center pb-2 border-b border-white/[0.08]">
              <span className="text-[11px] text-muted-foreground">Член семьи</span>
              <span className="text-[11px] text-muted-foreground flex items-center gap-1 w-16 justify-center"><MapPin size={12} /> Видит меня</span>
              <span className="text-[11px] text-muted-foreground flex items-center gap-1 w-16 justify-center"><Eye size={12} /> Вижу я</span>
            </div>
            <div className="max-h-[45vh] overflow-y-auto">
            {members.map((m) => (
              <div key={m.id} className="grid grid-cols-[1fr_auto_auto] gap-x-5 items-center py-2.5 border-b border-white/[0.05]" data-testid={`privacy-row-${m.id}`}>
                <span className="text-sm font-semibold truncate">{m.name}</span>
                <span className="w-16 flex justify-center">
                  <Switch checked={!!seesMe[m.id]} onCheckedChange={(v) => setSeesMe((s) => ({ ...s, [m.id]: v }))}
                    data-testid={`privacy-sees-me-${m.id}`} />
                </span>
                <span className="w-16 flex justify-center">
                  <Switch checked={!!iSee[m.id]} onCheckedChange={(v) => setISee((s) => ({ ...s, [m.id]: v }))}
                    data-testid={`privacy-i-see-${m.id}`} />
                </span>
              </div>
            ))}
            </div>
            <p className="text-[11px] text-muted-foreground pt-2">
              «Видит меня» — человек видит вашу метку и путь. «Вижу я» — показывать ли его на вашей карте.
            </p>
          </div>
        )}
        <Button onClick={save} className="w-full mt-2" data-testid="privacy-save-btn" disabled={members.length === 0}>Сохранить</Button>
      </DialogContent>
    </Dialog>
  );
}
